import { atom } from "jotai";
import type { PendingCustomerUser } from "shared";

export const pendingCustomerUsersAtom = atom<PendingCustomerUser[]>([]);

export const addPendingCustomerUserAtom = atom(
  null,
  (get, set, user: PendingCustomerUser) => {
    set(pendingCustomerUsersAtom, [...get(pendingCustomerUsersAtom), user]);
  },
);

export const updatePendingCustomerUserAtom = atom(
  null,
  (get, set, user: PendingCustomerUser) => {
    set(
      pendingCustomerUsersAtom,
      get(pendingCustomerUsersAtom).map((row) =>
        // Editing a row IS the user resolving its failure, so the reason goes.
        row.id === user.id ? { ...user, failedReason: null } : row,
      ),
    );
  },
);

export const removePendingCustomerUserAtom = atom(
  null,
  (get, set, id: string) => {
    set(
      pendingCustomerUsersAtom,
      get(pendingCustomerUsersAtom).filter((row) => row.id !== id),
    );
  },
);

/**
 * Reconcile the pending list against a batch-create response:
 *  - drop rows that WERE created, else they linger and can be re-sent;
 *  - stamp the server reason onto rows that FAILED so each card shows why
 *    inline rather than the reason living only in a 3-second toast.
 * Both sides match by email, case-insensitively.
 */
export const applyCustomerUserBatchResultAtom = atom(
  null,
  (
    get,
    set,
    result: {
      created: Array<{ email?: string | null }>;
      failed: Array<{ email?: string | null; reason?: string | null }>;
    },
  ) => {
    const key = (email?: string | null) => (email ?? "").trim().toLowerCase();

    const createdEmails = new Set(result.created.map((c) => key(c.email)));
    const failureByEmail = new Map(
      result.failed.map((f) => [key(f.email), f.reason ?? null]),
    );

    set(
      pendingCustomerUsersAtom,
      get(pendingCustomerUsersAtom)
        .filter((row) => !createdEmails.has(key(row.email)))
        .map((row) =>
          failureByEmail.has(key(row.email))
            ? {
                ...row,
                failedReason: failureByEmail.get(key(row.email)) ?? null,
              }
            : row,
        ),
    );
  },
);

export const resetPendingCustomerUsersAtom = atom(null, (_get, set) => {
  set(pendingCustomerUsersAtom, []);
});
